// frontend/src/pages/ProductDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { getProductBySlug } from "../api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import CurrencyDisplay from "../components/UI/CurrencyDisplay";
import Button from "../components/UI/Button";
import Loader from "../components/UI/Loader";
import ProductReactions from "../components/Product/ProductReactions";
import ProductComments from "../components/Product/ProductComments";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import {
  faArrowLeft,
  faExclamationTriangle,
  faTractor,
  faBox,
  faTag,
  faShippingFast,
  faCheckCircle,
  faSpinner,
  faMobileAlt,
  faMoneyBillWave,
  faClock,
  faShieldAlt,
  faPhone,
  faGlobe,
  faCalendarCheck,
  faStar,
  faStarHalfAlt,
  faRuler,
  faWeightScale,
  faInfoCircle,
  faBuilding,
  faMinus,
  faPlus,
  faHeart,
  faShare,
  faWhatsapp as faWhatsappSolid,
} from "@fortawesome/free-solid-svg-icons";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// MAIN COMPONENT
// ============================================
const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(0);

  const userType = user?.userType || "customer";
  const farmSize = user?.farmDetails?.farmSize || "small";
  const minOrderQuantity =
    userType === "farmer" ? product?.farmerMinimumOrder || 25 : 1;
  const isFarmer = userType === "farmer";

  const AVAILABLE_SIZES = product?.availableSizes || [2, 10, 25, 50];
  const PREORDER_SIZES = product?.preOrderSizes || [25, 50];

  // ============================================
  // FETCH PRODUCT
  // ============================================
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await getProductBySlug(slug);
        setProduct(data);
        if (userType === "farmer" && data.farmerMinimumOrder) {
          setQuantity(data.farmerMinimumOrder);
        }
      } catch (err) {
        console.error("Error fetching product:", err);
        setError(err.response?.data?.message || "Product not found");
        setTimeout(() => navigate("/products"), 3000);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug, navigate, userType]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleQuantityChange = (newQuantity) => {
    const minQty = isFarmer ? product?.farmerMinimumOrder || 25 : 1;
    const maxQty = product?.stock || 999;
    const step = isFarmer ? 25 : 1;
    const finalQuantity = Math.max(minQty, Math.min(maxQty, newQuantity));
    const roundedQuantity = Math.round(finalQuantity / step) * step || step;
    setQuantity(Math.max(minQty, roundedQuantity));

    if (product?.bulkOptions) {
      const matchingTier = [...product.bulkOptions]
        .filter((t) => t.isActive !== false)
        .reverse()
        .find((tier) => finalQuantity >= tier.quantity);
      setSelectedTier(matchingTier);
    }
  };

  const getPriceForQuantity = () => {
    if (!product) return 0;
    let discountPercent = 0;
    if (product.bulkOptions && product.bulkOptions.length > 0) {
      const applicableTier = [...product.bulkOptions]
        .filter((t) => t.isActive !== false)
        .reverse()
        .find((tier) => quantity >= tier.quantity);
      if (applicableTier) discountPercent = applicableTier.discountPercent;
    }
    if (product.priceTiers && product.priceTiers.length > 0) {
      const tierMatch = product.priceTiers
        .filter((t) => t.userType === userType)
        .find(
          (t) =>
            quantity >= t.minQuantity &&
            (t.maxQuantity === null || quantity <= t.maxQuantity),
        );
      if (tierMatch && tierMatch.discountPercent > discountPercent) {
        discountPercent = tierMatch.discountPercent;
      }
    }
    const basePrice = product.basePrice?.usd || product.pricing?.usd || 0;
    return basePrice * (1 - discountPercent / 100);
  };

  const getDiscountPercent = () => {
    if (selectedTier) return selectedTier.discountPercent;
    if (product?.bulkOptions && product.bulkOptions.length > 0) {
      const applicableTier = [...product.bulkOptions]
        .filter((t) => t.isActive !== false)
        .reverse()
        .find((tier) => quantity >= tier.quantity);
      if (applicableTier) return applicableTier.discountPercent;
    }
    if (product?.priceTiers) {
      const tierMatch = product.priceTiers
        .filter((t) => t.userType === userType)
        .find(
          (t) =>
            quantity >= t.minQuantity &&
            (t.maxQuantity === null || quantity <= t.maxQuantity),
        );
      if (tierMatch) return tierMatch.discountPercent;
    }
    return 0;
  };

  const handleAddToCart = async () => {
    if (!product) return;
    if (isFarmer && quantity < minOrderQuantity) {
      alert(`Minimum order for farmers is ${minOrderQuantity}kg`);
      return;
    }
    if (product.stock <= 0) {
      alert("This product is out of stock");
      return;
    }
    setAdding(true);
    try {
      await addItem(product._id, quantity);
      alert("Added to cart successfully!");
    } catch (err) {
      alert(err.response?.data?.message || "Error adding to cart");
    } finally {
      setAdding(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: `Check out ${product.name} at Kenali Enterprise`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) return <Loader />;
  if (error || !product) {
    return (
      <div className="container product-detail-error">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <h2>{error || "Product not found"}</h2>
        <p>Redirecting to products page...</p>
      </div>
    );
  }

  const pricePerUnit = getPriceForQuantity();
  const totalPrice = pricePerUnit * quantity;
  const discountPercent = getDiscountPercent();
  const stockStatus =
    product.stock > 0
      ? product.stock < 10
        ? "low-stock"
        : "in-stock"
      : "out-of-stock";
  const stockText =
    product.stock > 0
      ? product.stock < 10
        ? `Only ${product.stock} units left`
        : `${product.stock} units in stock`
      : "Out of stock";
  const isGrowPro = product.slug === "kenali-growpro";
  const isMalaysia =
    product.origin === "Malaysia" || product.origin === undefined;

  const bulkOptions =
    product.bulkOptions?.filter((t) => t.isActive !== false) || [];
  const images = product.images || [];

  const whatsappMessage = `Hi Kenali Enterprise, I'm interested in your product: ${product.name}. I want to order ${quantity}kg. Can you help me with the order process?`;

  return (
    <motion.div
      className="container product-detail-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* ========== BACK BUTTON ========== */}
      <button className="back-button" onClick={() => navigate(-1)}>
        <FontAwesomeIcon icon={faArrowLeft} /> Back
      </button>

      {/* ========== PRODUCT GRID ========== */}
      <div className="product-detail-grid">
        {/* ========== GALLERY ========== */}
        <div className="product-gallery">
          <div className="main-image">
            {images.length > 0 && images[activeImage] ? (
              <img src={images[activeImage]} alt={product.name} />
            ) : (
              <div className="image-placeholder">📦</div>
            )}
          </div>
          {images.length > 1 && (
            <div className="thumbnail-grid">
              {images.map((img, idx) => (
                <motion.div
                  key={idx}
                  className={`thumbnail-item ${activeImage === idx ? "active" : ""}`}
                  onClick={() => setActiveImage(idx)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <img src={img} alt={`${product.name} ${idx + 1}`} />
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* ========== INFO ========== */}
        <div className="product-info">
          <h1>
            {product.name}
            {isMalaysia && (
              <span className="origin-badge">
                <FontAwesomeIcon icon={faGlobe} /> Malaysia
              </span>
            )}
          </h1>
          <div className="product-category-tag">
            <span>{product.category?.replace(/-/g, " ")}</span>
            {product.supplierName && (
              <span className="supplier-tag">
                <FontAwesomeIcon icon={faBuilding} /> {product.supplierName}
              </span>
            )}
          </div>

          {/* Trust Badges */}
          <div className="trust-badges">
            <span className="trust-badge">
              <FontAwesomeIcon icon={faShieldAlt} /> NRA Registered
            </span>
            <span className="trust-badge">
              <FontAwesomeIcon icon={faCheckCircle} /> Quality Assured
            </span>
            {isMalaysia && (
              <span className="trust-badge malaysia">
                <FontAwesomeIcon icon={faGlobe} /> Malaysia Origin
              </span>
            )}
            {isGrowPro && (
              <span className="trust-badge preorder">
                <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order Available
              </span>
            )}
          </div>

          {/* Available Sizes */}
          {AVAILABLE_SIZES.length > 0 && (
            <div className="size-section">
              <h4>
                <FontAwesomeIcon icon={faRuler} /> Available Sizes
              </h4>
              <div className="size-tags">
                {AVAILABLE_SIZES.map((size) => (
                  <span
                    key={size}
                    className={`size-tag ${PREORDER_SIZES.includes(size) ? "preorder" : ""}`}
                  >
                    {size}kg
                    {PREORDER_SIZES.includes(size) && (
                      <span className="preorder-label">Pre-Order</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Price */}
          <div className="price-section">
            {discountPercent > 0 && (
              <span className="discount-badge">Save {discountPercent}%</span>
            )}
            <span className="current-price">
              <CurrencyDisplay amountUSD={pricePerUnit} />
            </span>
            <span className="unit">/{product.unit || "kg"}</span>
            {discountPercent > 0 && product.basePrice?.usd && (
              <span className="original-price">
                <CurrencyDisplay amountUSD={product.basePrice.usd} />
              </span>
            )}
          </div>

          {/* Farmer Notice */}
          {isFarmer && (
            <div
              className={`farmer-notice ${quantity < minOrderQuantity ? "warning" : ""}`}
            >
              <FontAwesomeIcon icon={faTractor} />
              <div>
                <strong>🌾 Farmer Bulk Pricing Active</strong>
                <p>Minimum order: {product.farmerMinimumOrder || 25}kg</p>
                {quantity < minOrderQuantity && (
                  <div className="warning-text">
                    ⚠️ Please adjust quantity to meet minimum order requirement
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Options */}
          <div className="payment-options">
            <strong>
              <FontAwesomeIcon icon={faMoneyBillWave} /> Payment Options:
            </strong>
            <span>
              Cash on Delivery • Orange Money • Afrimoney • WAVE • Bank Transfer
            </span>
          </div>

          {/* Bulk Discounts */}
          {bulkOptions.length > 0 && (
            <div className="bulk-discounts">
              <h4>
                <FontAwesomeIcon icon={faBox} /> Bulk Discounts
              </h4>
              <ul>
                {bulkOptions.map((option, idx) => (
                  <li
                    key={idx}
                    className={quantity >= option.quantity ? "active" : ""}
                  >
                    <span>Buy {option.quantity}kg</span>
                    <span>
                      Save {option.discountPercent}% →{" "}
                      <CurrencyDisplay
                        amountUSD={
                          (product.basePrice?.usd || 0) *
                          (1 - option.discountPercent / 100)
                        }
                        compact
                      />
                      /kg
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Description */}
          <div className="product-description">
            <p>{product.description}</p>
          </div>

          {/* Benefits */}
          {product.benefits && product.benefits.length > 0 && (
            <div className="product-benefits">
              <h4>Benefits</h4>
              <ul>
                {product.benefits.map((benefit, idx) => (
                  <li key={idx}>
                    <FontAwesomeIcon icon={faCheckCircle} />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Meta */}
          <div className="product-meta">
            {product.origin && (
              <p>
                <strong>
                  <FontAwesomeIcon icon={faGlobe} /> Origin:
                </strong>
                <span>{product.origin}</span>
              </p>
            )}
            {product.supplierName && (
              <p>
                <strong>
                  <FontAwesomeIcon icon={faBuilding} /> Supplier:
                </strong>
                <span>{product.supplierName}</span>
              </p>
            )}
            <p>
              <strong>
                <FontAwesomeIcon icon={faClock} /> Availability:
              </strong>
              <span className={`stock-status ${stockStatus}`}>{stockText}</span>
            </p>
            {product.packaging && (
              <p>
                <strong>
                  <FontAwesomeIcon icon={faBox} /> Packaging:
                </strong>
                <span>{product.packaging}</span>
              </p>
            )}
            {product.shippingWeight && (
              <p>
                <strong>
                  <FontAwesomeIcon icon={faWeightScale} /> Shipping Weight:
                </strong>
                <span>{product.shippingWeight}kg</span>
              </p>
            )}
          </div>

          {/* Specifications */}
          {product.specifications &&
            Object.keys(product.specifications).length > 0 && (
              <div className="specifications">
                <h4>
                  <FontAwesomeIcon icon={faInfoCircle} /> Specifications
                </h4>
                <ul>
                  {Object.entries(product.specifications).map(
                    ([key, value]) => (
                      <li key={key}>
                        <strong>{key}:</strong>
                        <span>{value}</span>
                      </li>
                    ),
                  )}
                </ul>
              </div>
            )}

          {/* Reactions */}
          <ProductReactions productId={product._id} />

          {/* ========== QUANTITY SELECTOR ========== */}
          <div className="quantity-selector">
            <div className="quantity-control">
              <label className="quantity-label">
                Quantity ({product.unit || "kg"})
              </label>
              <div className="quantity-input-wrapper">
                <button
                  className="qty-btn"
                  onClick={() =>
                    handleQuantityChange(quantity - (isFarmer ? 25 : 1))
                  }
                  disabled={quantity <= minOrderQuantity}
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
                <input
                  type="number"
                  min={minOrderQuantity}
                  max={product.stock}
                  step={isFarmer ? 25 : 1}
                  value={quantity}
                  onChange={(e) =>
                    handleQuantityChange(
                      parseInt(e.target.value) || minOrderQuantity,
                    )
                  }
                />
                <button
                  className="qty-btn"
                  onClick={() =>
                    handleQuantityChange(quantity + (isFarmer ? 25 : 1))
                  }
                  disabled={quantity >= product.stock}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>
            </div>

            <div className="total-price">
              <div className="total-label">Total</div>
              <div className="total-amount">
                <CurrencyDisplay amountUSD={totalPrice} />
              </div>
            </div>

            <button
              className="add-to-cart-btn"
              onClick={handleAddToCart}
              disabled={
                adding ||
                product.stock === 0 ||
                (isFarmer && quantity < minOrderQuantity)
              }
            >
              {adding ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Adding...
                </>
              ) : product.stock === 0 ? (
                "Out of Stock"
              ) : (
                "Add to Cart"
              )}
            </button>
          </div>

          {/* Contact Buttons */}
          <div className="contact-buttons">
            <a
              href={`https://wa.me/23231030808?text=${encodeURIComponent(whatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-btn"
            >
              <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Order
            </a>
            <a href="tel:+23231030808" className="call-btn">
              <FontAwesomeIcon icon={faPhone} /> Call Us
            </a>
            <button className="share-btn" onClick={handleShare}>
              <FontAwesomeIcon icon={faShare} /> Share
            </button>
          </div>

          {/* Bulk Contact */}
          {isFarmer && quantity >= 1000 && (
            <div className="bulk-contact">
              <FontAwesomeIcon icon={faShippingFast} />
              <span>
                For orders over 1000kg, contact us for custom pricing:{" "}
                <a href="mailto:kenalienterprisesl@gmail.com">
                  kenalienterprisesl@gmail.com
                </a>{" "}
                or{" "}
                <a
                  href="https://wa.me/23231030808"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  WhatsApp
                </a>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ========== COMMENTS ========== */}
      <ProductComments productId={product._id} />

      {/* ============================================
          STYLES
          ============================================ */}
      <style>{`
        .product-detail-page {
          padding: clamp(1rem, 2vw, 2rem) clamp(0.75rem, 2vw, 2rem);
        }

        .back-button {
          background: none;
          border: none;
          cursor: pointer;
          margin-bottom: 1.5rem;
          color: #D9A321;
          font-size: clamp(0.875rem, 1vw, 1rem);
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
          padding: 0.5rem 0;
          font-family: "Inter", sans-serif;
        }

        .back-button:hover {
          color: #B8860B;
          transform: translateX(-5px);
        }

        .product-detail-error {
          text-align: center;
          padding: 4rem 2rem;
        }

        .product-detail-error svg {
          font-size: 3rem;
          color: #D9A321;
          margin-bottom: 1rem;
        }

        .product-detail-error h2 {
          color: #006B2E;
          margin-bottom: 0.5rem;
        }

        .product-detail-error p {
          color: #5A5A5A;
        }

        .product-detail-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
        }

        @media (min-width: 768px) {
          .product-detail-grid {
            grid-template-columns: 1fr 1fr;
            gap: 3rem;
          }
        }

        /* ========== GALLERY ========== */
        .product-gallery {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .main-image {
          width: 100%;
          border-radius: 12px;
          overflow: hidden;
          background: #F8F8F8;
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .main-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .main-image img:hover {
          transform: scale(1.02);
        }

        .image-placeholder {
          font-size: 4rem;
          color: #D9A321;
        }

        .thumbnail-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0.75rem;
        }

        .thumbnail-item {
          border-radius: 8px;
          overflow: hidden;
          background: #F8F8F8;
          aspect-ratio: 1;
          cursor: pointer;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .thumbnail-item.active {
          border-color: #D9A321;
        }

        .thumbnail-item:hover {
          transform: scale(1.02);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .thumbnail-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        /* ========== INFO ========== */
        .product-info h1 {
          color: #006B2E;
          margin-bottom: 0.5rem;
          font-size: clamp(1.25rem, 2.5vw, 2rem);
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .origin-badge {
          font-size: 0.6rem;
          background: #e8f5e9;
          color: #006B2E;
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          font-weight: 500;
        }

        .product-category-tag {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .product-category-tag span {
          display: inline-block;
          background: rgba(217, 163, 33, 0.1);
          color: #D9A321;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: capitalize;
        }

        .supplier-tag {
          background: #f0f0f0 !important;
          color: #5A5A5A !important;
        }

        .trust-badges {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .trust-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.65rem;
          padding: 0.15rem 0.5rem;
          border-radius: 12px;
          background: #e8f5e9;
          color: #006B2E;
        }

        .trust-badge.malaysia {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .trust-badge.preorder {
          background: #fef3c7;
          color: #d97706;
        }

        .size-section {
          margin-bottom: 1rem;
        }

        .size-section h4 {
          font-size: 0.813rem;
          color: #1F1F1F;
          margin-bottom: 0.25rem;
        }

        .size-section h4 svg {
          margin-right: 0.25rem;
          color: #D9A321;
        }

        .size-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .size-tag {
          padding: 0.15rem 0.6rem;
          border-radius: 12px;
          font-size: 0.75rem;
          background: #f0f0f0;
          color: #1F1F1F;
          border: 1px solid #e5e5e5;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .size-tag.preorder {
          border-color: #D9A321;
          background: #fef3c7;
        }

        .preorder-label {
          font-size: 0.5rem;
          background: #D9A321;
          color: #fff;
          padding: 0.05rem 0.25rem;
          border-radius: 4px;
        }

        .price-section {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .price-section .discount-badge {
          display: inline-block;
          background: #16a34a;
          color: #ffffff;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .price-section .current-price {
          font-size: clamp(1.5rem, 2.5vw, 2rem);
          font-weight: 700;
          color: #D9A321;
        }

        .price-section .unit {
          font-size: 0.875rem;
          color: #5A5A5A;
        }

        .price-section .original-price {
          font-size: 0.875rem;
          color: #5A5A5A;
          text-decoration: line-through;
        }

        .farmer-notice {
          background: #e8f5e9;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          border: 1px solid #c8e6c9;
        }

        .farmer-notice svg {
          color: #006B2E;
          font-size: 1.25rem;
          margin-top: 0.125rem;
        }

        .farmer-notice strong {
          color: #006B2E;
        }

        .farmer-notice p {
          color: #5A5A5A;
          font-size: 0.875rem;
          margin: 0;
        }

        .farmer-notice.warning {
          background: #fef3c7;
          border-color: #fde68a;
        }

        .farmer-notice .warning-text {
          color: #92400e;
          font-size: 0.813rem;
          margin-top: 0.25rem;
        }

        .payment-options {
          background: #f8f9fa;
          padding: 0.5rem 0.75rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          border: 1px solid #e5e5e5;
          font-size: clamp(0.7rem, 0.9vw, 0.75rem);
          color: #5A5A5A;
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.25rem;
        }

        .payment-options strong {
          color: #006B2E;
        }

        .bulk-discounts {
          background: #F8F8F8;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .bulk-discounts h4 {
          font-size: 0.875rem;
          color: #006B2E;
          margin-bottom: 0.5rem;
        }

        .bulk-discounts h4 svg {
          margin-right: 0.5rem;
        }

        .bulk-discounts ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .bulk-discounts li {
          display: flex;
          justify-content: space-between;
          padding: 0.3rem 0;
          border-bottom: 1px solid #e5e5e5;
          font-size: 0.813rem;
          color: #5A5A5A;
        }

        .bulk-discounts li:last-child {
          border-bottom: none;
        }

        .bulk-discounts li.active {
          color: #006B2E;
          font-weight: 600;
        }

        .bulk-discounts li span:last-child {
          color: #D9A321;
        }

        .product-description {
          margin-bottom: 1rem;
        }

        .product-description p {
          color: #5A5A5A;
          line-height: 1.7;
          font-size: clamp(0.875rem, 1vw, 0.938rem);
        }

        .product-benefits {
          margin-bottom: 1rem;
        }

        .product-benefits h4 {
          font-size: 0.875rem;
          color: #006B2E;
          margin-bottom: 0.5rem;
        }

        .product-benefits ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .product-benefits li {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.2rem 0;
          font-size: 0.875rem;
          color: #5A5A5A;
        }

        .product-benefits li svg {
          color: #16a34a;
          font-size: 0.875rem;
        }

        .product-meta {
          background: #F8F8F8;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        .product-meta p {
          display: flex;
          justify-content: space-between;
          padding: 0.2rem 0;
          font-size: 0.813rem;
          color: #5A5A5A;
          border-bottom: 1px solid #e5e5e5;
        }

        .product-meta p:last-child {
          border-bottom: none;
        }

        .product-meta strong {
          color: #1F1F1F;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .stock-status {
          font-weight: 600;
          text-transform: capitalize;
        }

        .stock-status.in-stock {
          color: #16a34a;
        }

        .stock-status.low-stock {
          color: #f59e0b;
        }

        .stock-status.out-of-stock {
          color: #dc2626;
        }

        .specifications {
          margin-bottom: 1rem;
        }

        .specifications h4 {
          font-size: 0.875rem;
          color: #006B2E;
          margin-bottom: 0.5rem;
        }

        .specifications h4 svg {
          margin-right: 0.25rem;
        }

        .specifications ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .specifications li {
          display: flex;
          justify-content: space-between;
          padding: 0.3rem 0;
          border-bottom: 1px solid #f0f0f0;
          font-size: 0.813rem;
          color: #5A5A5A;
        }

        .specifications li:last-child {
          border-bottom: none;
        }

        .specifications li strong {
          color: #1F1F1F;
        }

        /* ========== QUANTITY ========== */
        .quantity-selector {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
          padding: 1rem;
          background: #F8F8F8;
          border-radius: 12px;
          margin-top: 1rem;
        }

        @media (min-width: 576px) {
          .quantity-selector {
            grid-template-columns: 1fr 1fr;
          }
        }

        .quantity-control {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .quantity-label {
          font-weight: 500;
          font-size: 0.813rem;
          color: #1F1F1F;
        }

        .quantity-input-wrapper {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .quantity-input-wrapper .qty-btn {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          border: 1px solid #e5e5e5;
          background: #ffffff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.8rem;
          transition: all 0.3s ease;
          color: #1F1F1F;
        }

        .quantity-input-wrapper .qty-btn:hover:not(:disabled) {
          border-color: #D9A321;
          background: #f8f8f8;
        }

        .quantity-input-wrapper .qty-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .quantity-input-wrapper input {
          width: 70px;
          padding: 0.5rem;
          border: 1px solid #e5e5e5;
          border-radius: 8px;
          text-align: center;
          font-size: 1rem;
          font-weight: 600;
          background: #ffffff;
          color: #1F1F1F;
        }

        .quantity-input-wrapper input:focus {
          outline: none;
          border-color: #D9A321;
        }

        .total-price {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
        }

        @media (min-width: 576px) {
          .total-price {
            align-items: flex-end;
          }
        }

        .total-label {
          font-size: 0.75rem;
          color: #5A5A5A;
        }

        .total-amount {
          font-size: clamp(1.25rem, 2vw, 1.5rem);
          font-weight: 700;
          color: #D9A321;
        }

        .add-to-cart-btn {
          grid-column: 1 / -1;
          padding: 0.75rem;
          background: #D9A321;
          color: #ffffff;
          border: none;
          border-radius: 8px;
          font-size: clamp(0.875rem, 1vw, 1rem);
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          min-height: 44px;
          font-family: "Inter", sans-serif;
        }

        .add-to-cart-btn:hover:not(:disabled) {
          background: #B8860B;
          transform: translateY(-2px);
        }

        .add-to-cart-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .contact-buttons {
          display: flex;
          gap: 0.75rem;
          margin-top: 0.75rem;
          flex-wrap: wrap;
        }

        .contact-buttons a,
        .contact-buttons button {
          flex: 1;
          min-width: 120px;
          padding: 0.6rem 1rem;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: clamp(0.813rem, 0.9vw, 0.875rem);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
          border: none;
          cursor: pointer;
          font-family: "Inter", sans-serif;
        }

        .whatsapp-btn {
          background: #25d366;
          color: #ffffff;
        }

        .whatsapp-btn:hover {
          background: #1da851;
        }

        .call-btn {
          background: #3b82f6;
          color: #ffffff;
        }

        .call-btn:hover {
          background: #2563eb;
        }

        .share-btn {
          background: #6b7280;
          color: #ffffff;
        }

        .share-btn:hover {
          background: #4b5563;
        }

        .bulk-contact {
          margin-top: 0.75rem;
          padding: 0.5rem 0.75rem;
          background: #e8f5e9;
          border-radius: 8px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: clamp(0.75rem, 0.85vw, 0.813rem);
          color: #006B2E;
          border: 1px solid #c8e6c9;
          flex-wrap: wrap;
        }

        .bulk-contact a {
          color: #D9A321;
          font-weight: 500;
        }

        .bulk-contact a:hover {
          text-decoration: underline;
        }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 768px) {
          .product-detail-page {
            padding: 1rem 0.75rem;
          }

          .product-gallery {
            gap: 0.75rem;
          }

          .product-info h1 {
            font-size: 1.25rem;
          }

          .price-section .current-price {
            font-size: 1.5rem;
          }

          .quantity-selector {
            padding: 0.75rem;
          }

          .quantity-input-wrapper input {
            width: 60px;
          }

          .thumbnail-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        @media (max-width: 480px) {
          .product-detail-grid {
            gap: 1.5rem;
          }

          .product-info h1 {
            font-size: 1.1rem;
          }

          .price-section .current-price {
            font-size: 1.25rem;
          }

          .quantity-selector {
            grid-template-columns: 1fr;
            gap: 0.5rem;
          }

          .total-price {
            align-items: flex-start;
          }

          .add-to-cart-btn {
            padding: 0.6rem;
            font-size: 0.813rem;
          }

          .contact-buttons {
            flex-direction: column;
          }

          .contact-buttons a,
          .contact-buttons button {
            min-width: auto;
          }

          .bulk-contact {
            flex-direction: column;
            text-align: center;
          }

          .trust-badges {
            gap: 0.25rem;
          }

          .trust-badge {
            font-size: 0.55rem;
            padding: 0.1rem 0.35rem;
          }

          .size-tag {
            font-size: 0.65rem;
            padding: 0.1rem 0.4rem;
          }

          .thumbnail-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 0.5rem;
          }
        }
      `}</style>
    </motion.div>
  );
};

export default ProductDetail;
